import {useState} from 'react'
import {Flash} from '@primer/react'
import {AlertFillIcon, StopIcon} from '@primer/octicons-react'
import {ForYouItem} from '../shared/ForYouItem'
import {ForYouLeadingIcon} from '../shared/ForYouLeadingIcon'
import {PendingChecksBody} from './shared/PendingChecksBody'
import {decideChecksItem} from './shared/checks-item-decider'
import {useMergeabilityData} from './use-mergeability-data'
import {ApproveWorkflowsButton} from '@github-ui/mergebox/components/sections/checks/ApproveWorkflowsButton'
import {WorkflowApprovalSubtitle} from '@github-ui/mergebox/components/sections/checks/WorkflowApprovalSubtitle'
import {checksSectionHeading} from '@github-ui/mergebox/components/sections/checks/checks-section-header-text'
import {usePullRequestAnalytics} from '../../../../hooks/use-pull-request-analytics'
import styles from '../shared/ForYouItem.module.css'

// Renders when workflow runs are awaiting approval and the viewer can approve them.
// Opens by default and surfaces the action to approve the pending workflow runs.
export function ApprovableChecks({variant}: {variant?: 'default' | 'embedded'}) {
  const data = useMergeabilityData()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  const decision = decideChecksItem(data)
  if (!data || decision.kind !== 'approvable') {
    return null
  }

  const {mergeBoxPullRequest, checksSection} = data
  const {workflowApproval} = decision

  return (
    <ForYouItem
      leadingVisual={<ForYouLeadingIcon icon={AlertFillIcon} variant="warning" />}
      title={checksSectionHeading[checksSection.sectionStatus]}
      itemName="pending-checks"
      padding="none"
      variant={variant}
      defaultOpen
      description={
        <WorkflowApprovalSubtitle
          pendingWorkflowApprovalRollup={workflowApproval}
          className={styles.description}
          onClick={() =>
            sendPullRequestAnalyticsEvent('overview.for_you_item_action', 'OVERVIEW_FOR_YOU_ITEM', {
              item_name: 'pending-checks',
              action_name: 'learn_about_approving_workflows',
            })
          }
        />
      }
      actionSlot={
        <ApproveWorkflowsButton
          headSha={mergeBoxPullRequest.headRefOid}
          onApprovalStart={() => {
            setErrorMessage(null)
            sendPullRequestAnalyticsEvent('overview.for_you_item_action', 'OVERVIEW_FOR_YOU_ITEM', {
              item_name: 'pending-checks',
              action_name: 'approve_workflows',
            })
          }}
          onError={setErrorMessage}
          size="small"
        />
      }
    >
      {errorMessage && (
        <Flash className="tmp-m-3" variant="danger">
          <StopIcon className="mr-2" />
          {errorMessage}
        </Flash>
      )}

      <PendingChecksBody
        data={data}
        itemName="pending-checks"
        emptyMessage="Workflow runs are awaiting approval. Check results will appear once the workflows begin running."
      />
    </ForYouItem>
  )
}
